import { getInitialState, updateState } from "@/utils/helpers/StoreUtil";
import DataProvider from "@/utils/helpers/DataProvider";

import { IVuexState } from "@/models/interfaces/store";
import { IClass, IClassPayload } from "@/models/interfaces/classes";

import { AxiosResponse, AxiosError } from "axios";
import { ActionTree, MutationTree, Module } from "vuex";

import { State as RootState } from "@/store";
import { IStudent } from "@/models/interfaces/students";
import { ITeacher } from "@/models/interfaces/teachers";
export interface State {
  students: IVuexState<IStudent[]>;
  classes: IVuexState<IClass[]>;
  classTeachers: IVuexState<ITeacher[]>;
}

export const actions: ActionTree<State, RootState> = {
  async fetchStudentsInClass({ commit }, classId: string) {
    commit("setStudents");

    return DataProvider.get(`classes/${classId}/users`)
      .then((response: AxiosResponse) => {
        commit("setStudents", response.data.users);
        return response.data.users;
      })
      .catch((error: AxiosError) => {
        commit("setStudents", error);
        return error;
      });
  },

  async fetchClassesForSchool({ commit }, schoolsIds: string[]) {
    commit("setClasses");

    const promises = schoolsIds.map(id =>
      DataProvider.get<{ classes: IClass[] }>(`schools/${id}/classes`)
    );

    try {
      const response = await Promise.allSettled(promises);
      const classes = response
        .map(resolvedPromise =>
          resolvedPromise.status === "fulfilled"
            ? resolvedPromise.value.data.classes
            : []
        )
        .flat();

      commit("setClasses", classes);
      return classes;
    } catch (error) {
      commit("setClasses", error);
      return error;
    }
  },

  async fetchTeachersForClass({ commit }, classId: string) {
    commit("setClassTeachers");

    return DataProvider.get<{ users: ITeacher[] }>(
      `classes/${classId}/teachers`
    )
      .then(response => {
        commit("setClassTeachers", response.data.users);
        return response.data.users;
      })
      .catch(error => {
        commit("setClassTeachers", error);
        return error;
      });
  },

  async createClass(_, payload: { schoolId: string; data: IClassPayload }) {
    return DataProvider.post(
      `schools/${payload.schoolId}/classes`,
      payload.data
    );
  },

  async editClass(_, payload: { data: IClassPayload; classId: string }) {
    return DataProvider.patch(`classes/${payload.classId}`, payload.data);
  },

  async deleteClasses({ state, commit }, classesIds: string[]) {
    try {
      const { classes } = state;
      const deleteRequests = classesIds.map(id =>
        DataProvider.delete(`classes/${id}`)
      );
      const filteredClasses = classes.data!.filter(
        ({ classId }) => !classesIds.includes(classId)
      );

      const result = await Promise.all(deleteRequests);
      commit("setClasses", filteredClasses);

      return result;
    } catch (error) {
      return error;
    }
  },

  async assignStudentsToClass(
    _,
    payload: { userIds: string[]; classId: string; subgroupId: string }
  ) {
    return DataProvider.put(`classes/${payload.classId}/users`, {
      userIds: payload.userIds,
      subGroups: payload.subgroupId
        ? [
            {
              subGroupId: payload.subgroupId,
              userIds: payload.userIds
            }
          ]
        : []
    });
  },

  async assignTeachersToClass(
    _,
    payload: { userIds: string[]; classId: string }
  ) {
    return DataProvider.put(`classes/${payload.classId}/teachers`, {
      userIds: payload.userIds
    });
  }
};

export const mutations: MutationTree<State> = {
  setStudents(state, data?: IStudent[] | AxiosError) {
    state.students = updateState(state.students, data);
  },

  setClasses(state, data?: IClass[] | AxiosError) {
    state.classes = updateState(state.classes, data);
  },

  setClassTeachers(state, data?: ITeacher[] | AxiosError) {
    state.classTeachers = updateState(state.classTeachers, data);
  }
};

const store: Module<State, RootState> = {
  namespaced: true,
  state: {
    students: getInitialState<IStudent[]>(),
    classes: getInitialState<IClass[]>(),
    classTeachers: getInitialState<ITeacher[]>()
  },
  mutations,
  actions
};

export default store;
